import { KeyboardEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import type { GameInfo } from '@/features/games/shared/types/game-info';
import { SearchResultListItem } from '@/features/games/shared/components/games-searchbar/search-result-list-item';

type GamesSearchResultsDropdownProps = {
  games: GameInfo[];
  isVisible: boolean;
  activeIndex: number;
  hasMoreResults: boolean;
  onSelect: (game: GameInfo) => void;
  onShowMore?: () => void;
  showMoreLabel: string;
  emptyLabel: string;
  showPrices?: boolean;
};

export const GamesSearchResultsDropdown = ({
  games,
  isVisible,
  activeIndex,
  hasMoreResults,
  onSelect,
  onShowMore,
  showMoreLabel,
  emptyLabel,
  showPrices = false,
}: GamesSearchResultsDropdownProps) => {
  if (!isVisible) {
    return null;
  }

  const handleShowMoreKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onShowMore) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onShowMore();
    }
  };

  const hasGames = games.length > 0;

  return (
    <div className='border-border bg-background-panel absolute z-50 w-full overflow-hidden border border-solid p-0'>
      <div
        className='max-h-[80vh] w-full scrollbar-thin overflow-y-auto overscroll-contain'
        role='listbox'
        id='search-results-list'
      >
        {hasGames ? (
          <ul className='divide-y divide-gray-700'>
            {games.map((game, index) => (
              <SearchResultListItem
                key={game.id}
                game={game}
                isActive={activeIndex === index}
                onSelect={onSelect}
                showPrice={showPrices}
              />
            ))}
          </ul>
        ) : (
          <div
            className='text-md font-sans-bold text-text-muted p-4'
            role='status'
            aria-live='polite'
          >
            {emptyLabel}
          </div>
        )}

        {hasGames && hasMoreResults && (
          <div
            className='border-border bg-background-panel text-primary hover:bg-background-hover focus:bg-background-hover flex cursor-pointer items-center justify-center border-t p-3 focus:outline-hidden'
            onClick={onShowMore}
            role='button'
            tabIndex={0}
            onKeyDown={handleShowMoreKeyDown}
          >
            {showMoreLabel} <ChevronRight className='ml-1' size={16} />
          </div>
        )}
      </div>
    </div>
  );
};
